"use client";

import React, { useEffect, useRef, useState } from 'react';
import {
    AllSettings,
    Config,
    WebRtcDisconnectedEvent,PlayStreamRejectedEvent,
    PixelStreaming
} from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.6';

import { useStore } from '@/stores/store-provider';
import { observer } from 'mobx-react';
import { StreamingStateEnum } from '@/data/webrtc-enums';
import { a2fStreamer } from '@/services/a2f-streamer';

import "@/styles/components/pixel-streaming/pixel-streaming-wrapper.css"

const ARCWARE_SHARE_ID = process.env.NEXT_PUBLIC_ARCWARE_SHARE_ID;

const PS_LOG_FILTER_PATTERNS = [
    '[Info] - Message incoming:',
    '[Info] - DataChannelReceiveMessageType.VideoEncoderAvgQP',
    '[Debug] - Protocol sent =>',
    '[Debug] - Protocol received =>',
    'DataChannelReceiveMessageType.VideoEncoderAvgQP',
    'Message incoming:',
    'Protocol sent =>',
    'Protocol received =>',
    'LoggerType.Info (Logger.js:',
    'LoggerType.Debug (Logger.js:',
    'WebRtcPlayerController.js:',
    'DataChannelController.js:',
    'SignallingProtocol.js:',
    'KeepaliveMonitor.js:',
    'WebSocketTransport.js:',
    'Attempting keep-alive ping...',
];

function shouldFilterLog(message: string): boolean {
    return PS_LOG_FILTER_PATTERNS.some(p => message.includes(p)) ||
        (message.includes('"type": "ping"') && message.includes('"time":')) ||
        (message.includes('"type": "pong"') && message.includes('"time":'));
}

export interface PixelStreamingWrapperProps {
    initialSettings?: Partial<AllSettings>;
}

export const PixelStreamingWrapper = observer(function PixelStreamingWrapper({
    initialSettings
}: PixelStreamingWrapperProps) {
    const videoParent = useRef<HTMLDivElement>(null);

    const store = useStore();
    const digitalHumanStore = store.digitalHumanStore;

    const onConnectClick = () => {
        digitalHumanStore.connect();
    }

    function isClickToPlayVisible() {
        return digitalHumanStore.streamingState != StreamingStateEnum.StreamConnected;
    }

    const [pixelStreaming, setPixelStreaming] = useState<PixelStreaming>();

    // Run on component mount:
    useEffect(() => {

        if (videoParent.current == undefined) {
            return;
        }

        const originalConsoleInfo = console.info;
        const originalConsoleLog = console.log;
        
        (window as any).__originalConsoleInfo = originalConsoleInfo;
        (window as any).__originalConsoleLog = originalConsoleLog;
        
        const isPixelStreamingNoise = (args: any[]): boolean => {
            const message = args.join(' ');
            return (
                message.includes('Sending custom UIInteraction message') ||
                message.includes('[Info] - Message Sent:') ||
                message.includes('Message Sent:') ||
                message.includes('[Info] - Message incoming:') ||
                message.includes('[Info] - DataChannelReceiveMessageType') ||
                message.includes('[Debug] - Protocol sent =>') ||
                message.includes('[Debug] - Protocol received =>') ||
                message.includes('DataChannelReceiveMessageType') ||
                message.includes('Message incoming:') ||
                message.includes('Protocol sent =>') ||
                message.includes('Protocol received =>') ||
                message.includes('Logger.js:') ||
                message.includes('Logger.ts:') ||
                message.includes('WebRtcPlayerController.js:') ||
                message.includes('WebRtcPlayerController.ts:') ||
                message.includes('DataChannelController.js:') ||
                message.includes('DataChannelController.ts:') ||
                message.includes('DataChannelSender.ts:') ||
                message.includes('SendMessageController.ts:') ||
                message.includes('SignallingProtocol.js:') ||
                message.includes('SignallingProtocol.ts:') ||
                message.includes('KeepaliveMonitor.js:') ||
                message.includes('KeepaliveMonitor.ts:') ||
                message.includes('WebSocketTransport.js:') ||
                message.includes('WebSocketTransport.ts:') ||
                message.includes('Attempting keep-alive ping...') ||
                (message.includes('"type": "ping"') && message.includes('"time":')) ||
                (message.includes('"type": "pong"') && message.includes('"time":'))
            );
        };

        console.info = function(...args: any[]) {
            if (!isPixelStreamingNoise(args)) originalConsoleInfo.apply(console, args);
        };

        console.log = function(...args: any[]) {
            if (!isPixelStreamingNoise(args)) originalConsoleLog.apply(console, args);
        };

        // Track Arcware cleanup handle
        let arcwareAppRootElement: HTMLElement | null = null;
        let restoreWsSend: (() => void) | null = null;

        let streaming: PixelStreaming;

        if (ARCWARE_SHARE_ID) {
            // --- Arcware Cloud mode ---
            console.log('[PixelStreaming] Arcware mode — shareId:', ARCWARE_SHARE_ID);

            // The Arcware SDK has internal race conditions where it calls
            // send() while the socket is still CONNECTING or already CLOSING.
            // These throw InvalidStateError but are non-fatal — the SDK
            // handles reconnection internally. Wrap to suppress the error.
            const NativeWsSend = WebSocket.prototype.send;
            WebSocket.prototype.send = function(this: WebSocket, ...args: any[]) {
                try {
                    return NativeWsSend.apply(this, args as any);
                } catch (e: any) {
                    if (e?.name === 'InvalidStateError') {
                        return;
                    }
                    throw e;
                }
            };
            restoreWsSend = () => { WebSocket.prototype.send = NativeWsSend; };

            const { ArcwareInit } = require('@arcware-cloud/pixelstreaming-websdk');
            const { ss: _discardSs, ...arcwareInitialSettings } = initialSettings || {} as any;
            const { PixelStreaming: arcwareStream, Application: arcwareApp } = ArcwareInit(
                { shareId: ARCWARE_SHARE_ID },
                {
                    initialSettings: {
                        ...arcwareInitialSettings,
                        AutoConnect: false,
                        AutoPlayVideo: true,
                    },
                    settings: {
                        infoButton: false,
                        micButton: false,
                        audioButton: false,
                        fullscreenButton: false,
                        settingsButton: false,
                        connectionStrengthIcon: false,
                    }
                }
            );

            arcwareAppRootElement = arcwareApp.rootElement;
            videoParent.current.appendChild(arcwareAppRootElement);

            // ArcwarePixelStreaming extends Epic's PixelStreaming
            streaming = arcwareStream as unknown as PixelStreaming;

            // Hook Arcware-specific events for status updates
            const arcware = arcwareStream as any;

            if (arcware.queueHandler) {
                arcware.queueHandler.add((msg: any) => {
                    const q = msg?.queue ?? msg;
                    if (q?.index != null && q?.queueLength != null) {
                        const pos = q.index + 1;
                        let text = `Waiting in queue (${pos} of ${q.queueLength})`;
                        if (q.estimatedWaitTime != null) {
                            const unit = q.valueType ?? 'seconds';
                            text += ` — est. ${Math.ceil(q.estimatedWaitTime)} ${unit}`;
                        }
                        digitalHumanStore.setConnectionStatusMessage(text);
                    } else {
                        digitalHumanStore.setConnectionStatusMessage('Waiting for available session…');
                    }
                });
            }

            if (arcware.videoInitializedHandler) {
                arcware.videoInitializedHandler.add(() => {
                    digitalHumanStore.setConnectionStatusMessage(null);
                });
            }

            if (arcware.loveLetterHandler) {
                arcware.loveLetterHandler.add((msg: any) => {
                    if (msg?.reason) {
                        console.log('[PixelStreaming] Arcware love letter:', msg.reason);
                        const text = (msg.reason as string).replace(/^LL:\s*/, '');
                        if (text) {
                            digitalHumanStore.setConnectionStatusMessage(text);
                        }
                    }
                });
            }

            // Do NOT connect here — the user click in home.tsx triggers the
            // single connect() call via digitalHumanStore.connect(). This avoids
            // a race condition where a second connect() invalidates the in-flight
            // Arcware session, and also satisfies the browser's autoplay policy
            // which requires a user gesture for video with audio.
            console.log('[PixelStreaming] Arcware mode initialized, waiting for user to start');
        } else {
            // --- Direct Epic Pixel Streaming mode ---
            const wsUrl = process.env.NEXT_PUBLIC_WS_ENDPOINT;
            console.log('[PixelStreaming] Direct mode — WebSocket URL:', wsUrl);

            const config = new Config({ 
                initialSettings: {
                    ...initialSettings,
                    ss: wsUrl,
                    StreamerId: process.env.NEXT_PUBLIC_WS_STREAMER_ID || undefined,
                    AutoConnect: false,
                    WaitForStreamer: true,
                    TimeoutIfIdle: false
                }
            });

            streaming = new PixelStreaming(config, {
                videoElementParent: videoParent.current
            });

            setTimeout(() => {
                console.log('[PixelStreaming] Direct: initiating connection to', wsUrl);
                streaming.connect();
            }, 1000);
        }

        streaming.addEventListener('webRtcFailed', (error) => {
            console.error('Streaming error:', error);
        });

        // Add connection error handler
        streaming.addEventListener('webRtcFailed', (error) => {
            console.error('WebRTC Error:', error);
        });

        streaming.addEventListener('webRtcAutoConnect', () => {
            console.log(`webRtcAutoConnect`);
        });
        streaming.addEventListener('webRtcConnecting', () => {
            console.log(`webRtcConnecting`);
            digitalHumanStore.setStreamingState(StreamingStateEnum.WebRtcConnetting);
            if (ARCWARE_SHARE_ID) {
                digitalHumanStore.setConnectionStatusMessage('Establishing WebRTC connection…');
            }
        });
        streaming.addEventListener('webRtcConnected', () => {
            console.log(`webRtcConnected`);
            digitalHumanStore.setStreamingState(StreamingStateEnum.WebRtcConnected);
            if (ARCWARE_SHARE_ID) {
                digitalHumanStore.setConnectionStatusMessage('Loading video stream…');
            }
            
            // Connect A2F streamer to Pixel Streaming for audio forwarding
            a2fStreamer.setStreamer(streaming);
            console.log('🎬 A2F streamer connected to Pixel Streaming');
        });
        streaming.addEventListener('webRtcFailed', () => {
            console.log(`webRtcFailed`);
        });
        streaming.addEventListener( 'webRtcDisconnected', (event: WebRtcDisconnectedEvent) => {
            digitalHumanStore.setStreamingState(StreamingStateEnum.WebRtcDisconnected);
            digitalHumanStore.setConnectionStatusMessage(null);
            console.log(`webRtcDisconnected: ${event.data.eventString}`);
            
            // Disconnect A2F streamer from Pixel Streaming
            a2fStreamer.setStreamer(null);
            console.log('🎬 A2F streamer disconnected from Pixel Streaming');
        });
        streaming.addEventListener('streamLoading', () => {
             console.log(`streamLoading`);
             if (ARCWARE_SHARE_ID) {
                 digitalHumanStore.setConnectionStatusMessage('Loading video stream…');
             }
        });
        streaming.addEventListener('videoInitialized', () => {
            console.log(`videoInitialized`);
            digitalHumanStore.setConnectionStatusMessage(null);
        });
        streaming.addEventListener('playStreamError', ({ data: { message } }) => {
            console.log(`playStreamError ${message}`);
        });
        streaming.addEventListener('playStreamRejected', (event: PlayStreamRejectedEvent) => {
            console.log(`playStreamRejected: ${event.data.reason}`);
        });

        // Save the library instance into component state so that it can be accessed later:
        setPixelStreaming(streaming);

        digitalHumanStore.setStreamer(streaming);
        if (process.env.NEXT_PUBLIC_START_VIDEO_MUTED 
            && JSON.parse(process.env.NEXT_PUBLIC_START_VIDEO_MUTED)) {
            streaming.muteMicrophone();
        }

        return () => {
            try {
                if ((window as any).__originalConsoleInfo) {
                    console.info = (window as any).__originalConsoleInfo;
                    delete (window as any).__originalConsoleInfo;
                }
                if ((window as any).__originalConsoleLog) {
                    console.log = (window as any).__originalConsoleLog;
                    delete (window as any).__originalConsoleLog;
                }
                
                if (streaming) {
                    streaming.removeResponseEventListener("state");
                    streaming.disconnect();
                    digitalHumanStore.setStreamer(null);
                    digitalHumanStore.setStreamingState(StreamingStateEnum.WebRtcDisconnected);
                    a2fStreamer.setStreamer(null);
                }

                if (restoreWsSend) restoreWsSend();

                if (arcwareAppRootElement && videoParent.current?.contains(arcwareAppRootElement)) {
                    videoParent.current.removeChild(arcwareAppRootElement);
                }
            } catch (error) {
                console.error('Error during cleanup:', error);
            }
        };
    }, []);

    // Add effect to constrain video elements
    useEffect(() => {
        const constrainVideoElements = () => {
            const elements = videoParent.current?.querySelectorAll('video, audio, canvas, #streamingVideo');
            // Style video/canvas to fit container
            elements?.forEach((element: any) => {
                if (element.tagName === 'VIDEO' || element.tagName === 'CANVAS') {
                    element.style.width = '100%';
                    element.style.height = '100%';
                    element.style.maxWidth = '100%';
                    element.style.maxHeight = '100%';
                    element.style.position = 'relative';
                    element.style.top = '0';
                    element.style.left = '0';
                    element.style.right = 'auto';
                    element.style.bottom = 'auto';
                    element.style.transform = 'none';
                    element.style.objectFit = 'cover';
                }
            });

            // Ensure only one media element outputs audio to prevent echoes
            const mediaEls = videoParent.current?.querySelectorAll('video, audio');
            let firstUnmutedFound = false;
            mediaEls?.forEach((el: any) => {
                try {
                    // Prefer the first <video> to be the audible source
                    if (!firstUnmutedFound && el.tagName === 'VIDEO') {
                        el.muted = false;
                        el.volume = 1.0;
                        el.playsInline = true;
                        firstUnmutedFound = true;
                    } else {
                        el.muted = true;
                        el.volume = 0.0;
                    }
                } catch {}
            });
        };

        // Run immediately
        constrainVideoElements();

        // Set up observer to watch for new video elements
        const observer = new MutationObserver(constrainVideoElements);
        if (videoParent.current) {
            observer.observe(videoParent.current, { childList: true, subtree: true });
        }

        return () => observer.disconnect();
    }, []);

    return (
        <div className='h-full relative overflow-hidden' style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
            <div className='h-full w-full overflow-hidden' ref={videoParent} style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }} />
            {isClickToPlayVisible() && (
                <div className='absolute w-full h-full top-0 left-0 flex items-center justify-center cursor-pointer'
                    onClick={onConnectClick} >
                    <div>Click to play</div>
                </div>
            )}
        </div>
    );
});
